import React, { ChangeEvent, useEffect, useState } from 'react';
import {
  Box,
  Checkbox,
  Menu,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { green, grey, grey10, redDefault, yellow } from 'app/components/ThemeProvider/colors';
import DeleteModal from 'app/components/DeleteModal';
import useEmployeesTable from 'app/pages/SalaryPage/EmployeesTable/useEmployeesTable';
import TableDotsIcon from 'app/components/Icons/TableDotsIcon';
import { useChangeWorkersStatus } from 'app/utils/services/salaryServices';
import { Workers } from 'app/utils/services/types/salary.type';
import LogoutIcon from 'app/components/Icons/LogoutIcon';
import FlipBackward from 'app/components/Icons/FlipBackwar';

import CheckModal from '../CheckModal';

import {
  checkBox,
  CustomMenuItem,
  tableCell,
  tableCellText,
  tableContainer,
  tableHeaderCell,
  tableRowStyle,
} from './EmployeesTable.sx';
export interface WorkersCheck extends Workers {
  checked: boolean;
}

interface Props {
  workers: Workers[];
  amounts?: any;
  setAmounts?: any;
  handleChildData?: (data: WorkersCheck[]) => void;
  refetchWorkers?: () => void;
}

const EmployeesTable = (props: Props) => {
  const { amounts, setAmounts, workers, handleChildData, refetchWorkers } = props;
  const changeWordersStatus = useChangeWorkersStatus();

  const [deleteModal, setDeleteModal] = useState(false);
  const [isSelectAll, setIsSelectAll] = useState<boolean>(false);
  const [checkModal, setCheckModal] = useState(false);

  const {
    tableHeader,
    tableRow,
    isUnEmployeList,
    anchorEl,
    activeAnchor,
    handleClose,
    handleMenuClick,
    selectedWorkerId,
    isEmployeList,
  } = useEmployeesTable({
    amounts,
    setAmounts,
  });

  const [tableRows, setTableRows] = useState<WorkersCheck[]>(() => {
    return workers?.map((row) => {
      return {
        ...row,
        checked: false,
      };
    });
  });
  const handleSelectAll = (event: ChangeEvent<HTMLInputElement>) => {
    setIsSelectAll(event.target.checked);
    setTableRows((prevState) =>
      prevState.map((row) => {
        return {
          ...row,
          checked: event.target.checked,
        };
      })
    );
  };
  const handleSelect = (event: ChangeEvent<HTMLInputElement>, id: string) => {
    const checked = event.target.checked;
    setTableRows(
      tableRows.map((row) => {
        if (row.id === id) {
          return {
            ...row,
            checked,
          };
        }

        return row;
      })
    );
  };

  useEffect(() => {
    handleChildData && handleChildData(tableRows?.filter((row) => row.checked));
  }, [tableRows]);

  const changeWorkersStatusToUnList = () => {
    changeWordersStatus.mutate(
      {
        id_list: [selectedWorkerId],
        action: isEmployeList ? 'unlist' : 'to_list',
      },
      {
        onSuccess: () => {
          setCheckModal(false);
          refetchWorkers();
        },
      }
    );
  };

  return (
    <TableContainer sx={tableContainer(tableRow.length === 0)}>
      <Table>
        <TableHead>
          <TableRow sx={tableRowStyle}>
            <TableCell sx={{ ...tableHeaderCell, width: '42px', alignItems: 'flex-start' }}>
              {tableRow.length > 0 && (
                <Checkbox checked={isSelectAll} onChange={(e) => handleSelectAll(e)} sx={checkBox} />
              )}
            </TableCell>
            {tableHeader.map((item) => (
              <TableCell key={item.id} sx={{ ...tableHeaderCell }}>
                <Typography sx={{ fontSize: '15px', color: grey, lineHeight: '1' }}>{item.name}</Typography>
              </TableCell>
            ))}
            <TableCell sx={{ ...tableCell, width: '75px' }}></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableRows?.length !== 0 &&
            tableRows?.map((row, index) => (
              <TableRow
                key={row.id}
                sx={
                  (index + 1) % 2 !== 0
                    ? {
                        ...tableRowStyle,
                        background: grey10,
                      }
                    : tableRowStyle
                }
              >
                <TableCell sx={{ ...tableHeaderCell, width: '42px', alignItems: 'flex-start' }}>
                  <Checkbox checked={row?.checked} onChange={(e) => handleSelect(e, row.id)} sx={checkBox} />
                </TableCell>
                <TableCell sx={{ ...tableCell }}>
                  <Typography sx={tableCellText}>{row.full_name}</Typography>
                </TableCell>
                <TableCell sx={{ ...tableCell }}>
                  <Typography sx={tableCellText}>{row.iban}</Typography>
                </TableCell>
                <TableCell sx={{ ...tableCell, flexDirection: 'row', alignItems: 'center', gap: '12px' }}>
                  <Typography sx={tableCellText}>{row.expire_date}</Typography>
                </TableCell>
                <TableCell
                  sx={{
                    ...tableCell,
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: '12px',
                    justifyContent: 'flex-start',
                    width: '265px',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {row.state === 'ACTIVE' && (
                      <>
                        <Box sx={{ width: '12px', height: '12px', borderRadius: '50%', background: green }}></Box>
                        <Typography>Aktiv</Typography>
                      </>
                    )}
                    {row.state === 'BLOCKED' && (
                      <>
                        <Box sx={{ width: '12px', height: '12px', borderRadius: '50%', background: yellow }}></Box>
                        <Typography>Bloklaşdırılmış</Typography>
                      </>
                    )}
                    {row.state === 'CLOSED' && (
                      <>
                        <Box sx={{ width: '12px', height: '12px', borderRadius: '50%', background: redDefault }}></Box>
                        <Typography>Bağlı</Typography>
                      </>
                    )}
                  </Box>
                </TableCell>
                <TableCell
                  sx={{
                    p: '21px 0',
                    border: 'none',
                    display: 'flex',
                    alignItems: 'center',
                    width: '75px',
                    justifyContent: 'flex-end',
                  }}
                >
                  <TableDotsIcon
                    sx={{ '&:hover': { cursor: 'pointer' } }}
                    onClick={(event: any) => handleMenuClick(event, row.id)}
                    aria-controls={anchorEl ? `actions-${row.id}` : undefined}
                    aria-haspopup="true"
                    aria-expanded={anchorEl ? 'true' : undefined}
                  />
                </TableCell>
              </TableRow>
            ))}
          <Menu
            id={activeAnchor}
            disableAutoFocusItem
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            sx={{ '& .MuiPaper-root': { right: '30px', left: 'auto !important' } }}
          >
            <CustomMenuItem isUnEmployeList={isUnEmployeList} onClick={() => setCheckModal(true)}>
              {isUnEmployeList ? <FlipBackward /> : <LogoutIcon color={'warning'} />}{' '}
              {isUnEmployeList ? 'Siyahıya qaytar' : 'Siyahıdan çıxart'}
            </CustomMenuItem>
          </Menu>
        </TableBody>
      </Table>
      <DeleteModal
        open={deleteModal}
        close={setDeleteModal}
        headerText={'Əlavə edilmiş əməkdaşı silmək istəyirsiniz?'}
      />
      <CheckModal
        showText={false}
        open={checkModal}
        close={setCheckModal}
        title={
          isEmployeList
            ? 'Bu işçini siyahıdan çıxarmaq istədiyinizə əminsiniz?'
            : 'Bu işçini siyahıya geri qaytarmağa istədiyinizə əminsiniz?'
        }
        approveAction={changeWorkersStatusToUnList}
      />
    </TableContainer>
  );
};

export default EmployeesTable;
